import {
  CreateUserDto,
  GetUsersBoQueryDto,
  GetUsersBoResponse,
  UpdateUserDto,
  UpdateUserPasswordDto,
} from "../types/api/api-types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { axiosApi } from "./axios";
import { sortByRole } from "@/lib/utils";

const deleteUser = async (id: string) => {
  return axiosApi.delete(`/users/${id}`);
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("boClients");
  return useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success(t("userApi.deleteSuccess"));
    },
    onError: () => {
      toast.error(t("userApi.deleteError"));
    },
  });
};

const deleteUserBo = async (id: string) => {
  return axiosApi.delete(`/users/bo/${id}`);
};

export const useDeleteUserBo = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("boClients");
  return useMutation({
    mutationFn: deleteUserBo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["usersBo"] });
      toast.success(t("userApi.deleteSuccess"));
    },
    onError: () => {
      toast.error(t("userApi.deleteError"));
    },
  });
};

const createUser = async (data: CreateUserDto) => {
  return axiosApi.post(`/users`, data);
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("boClients");
  return useMutation({
    mutationFn: createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success(t("userApi.createSuccess"));
    },
    onError: () => {
      toast.error(t("userApi.createError"));
    },
  });
};

const createUserBo = async (data: CreateUserDto) => {
  return axiosApi.post(`/users/bo`, data);
};

export const useCreateUserBo = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("boClients");
  return useMutation({
    mutationFn: createUserBo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["usersBo"] });
      toast.success(t("userApi.createSuccess"));
    },
    onError: () => {
      toast.error(t("userApi.createError"));
    },
  });
};

const updateUser = async (payload: { id: string; data: UpdateUserDto }) => {
  return axiosApi.patch(`/users/${payload.id}`, payload.data);
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("boClients");
  return useMutation({
    mutationFn: updateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success(t("userApi.updateSuccess"));
    },
    onError: () => {
      toast.error(t("userApi.updateError"));
    },
  });
};

const updateUserBo = async (payload: { id: string; data: UpdateUserDto }) => {
  return axiosApi.patch(`/users/bo/${payload.id}`, payload.data);
};

export const useUpdateUserBo = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("boClients");
  return useMutation({
    mutationFn: updateUserBo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["usersBo"] });
      toast.success(t("userApi.updateSuccess"));
    },
    onError: () => {
      toast.error(t("userApi.updateError"));
    },
  });
};

const updateUserPassword = async (payload: { id: string; data: UpdateUserPasswordDto }) => {
  return axiosApi.patch(`/users/password/` + payload.id, payload.data);
};

export const useUpdateUserPassword = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("boClients");
  return useMutation({
    mutationFn: updateUserPassword,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success(t("userApi.passwordUpdateSuccess"));
    },
    onError: () => {
      toast.error(t("userApi.passwordUpdateError"));
    },
  });
};

const updateUserPasswordBo = async (payload: { id: string; data: UpdateUserPasswordDto }) => {
  return axiosApi.patch(`/users/bo/password/` + payload.id, payload.data);
};

export const useUpdateUserPasswordBo = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("boClients");
  return useMutation({
    mutationFn: updateUserPasswordBo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["usersBo"] });
      toast.success(t("userApi.passwordUpdateSuccess"));
    },
    onError: () => {
      toast.error(t("userApi.passwordUpdateError"));
    },
  });
};

const getUsers = async () => {
  const ret = await axiosApi.get<GetUsersBoResponse>(`/users`, { params: { isDeleted: false } });
  return ret.data.sort(sortByRole);
};

export const useGetUsers = (isBo?: boolean) => {
  return useQuery({
    queryKey: ["users"],
    queryFn: () => getUsers(),
    enabled: isBo !== true,
  });
};

const getCompanyUsersBo = async (companyId: string) => {
  const ret = await axiosApi.get<GetUsersBoResponse>(`/users/bo/${companyId}`);
  return ret.data.sort(sortByRole);
};

export const useGetCompanyUsersBo = (companyId: string) => {
  return useQuery({
    queryKey: ["users", companyId],
    queryFn: () => getCompanyUsersBo(companyId),
  });
};

const getUsersBo = async (query: GetUsersBoQueryDto) => {
  const ret = await axiosApi.get<GetUsersBoResponse>(`/users/bo`, { params: query });
  return ret.data.sort(sortByRole);
};

export const useGetUsersBo = (query: GetUsersBoQueryDto = {}) => {
  return useQuery({
    queryKey: ["usersBo", query],
    queryFn: () => getUsersBo(query),
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60, // 1 hour
    refetchOnWindowFocus: false,
  });
};
